import Axios from 'axios';
import { readMeta } from '../lib/page';
import { refreshedJwt } from './jwt';
import { Account, AccountData } from '../models/account';
import Profile from './public_api/Profile';
import CloseTheLoop from './public_api/CloseTheLoop';

interface ApiRole {
  'com.sittercity.rbac:Role': string;
}

export default class PublicApiClient {
  apiUrl: string;

  constructor() {
    const u = readMeta('sc_public_api_url');
    if (!u) throw new Error('api url not available');

    this.apiUrl = u;
  }

  async fetchData(pathname: string): Promise<any> {
    const token = await refreshedJwt();
    if (!token) return null;

    const u = new URL(this.apiUrl);
    u.pathname = pathname;

    const response = await Axios.get(u.toString(), {
      headers: {
        Accept: 'application/json; version=2',
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    }).catch((err) => console.error(err));

    return response;
  }

  public async account(): Promise<Account | null | undefined> {
    const d = await this.fetchData('/my-account');
    if (d === undefined) {
      return undefined;
    } // undefined if endpoint error
    if (d?.status !== 200) return null;

    const data = d.data['com.sittercity.account:Account'];

    const c = data['com.sittercity.account:Contact'];
    const r = data['com.sittercity.rbac:Roles'];

    let roles: string[] = [];
    if (r) {
      roles = r.map((v: ApiRole) => v['com.sittercity.rbac:Role']);
    }

    return new Account({
      ssoUUID: c.sso_uuid,
      id: c.sso_uuid,
      firstName: c.first_name,
      lastName: c.last_name,
      email: c.email,
      originalPhoto: c.original_photo_url,
      croppedPhoto: c.cropped_photo_url,
      location: c.location,
      hasPremiumHistory: c.has_premium_history,
      profilePhotoStatus: c.profile_photo_status,
      roles,
    } as AccountData);
  }

  public async profiles(): Promise<Profile | null | undefined> {
    const d = await this.fetchData('/my-profile');
    if (d === undefined) {
      return undefined;
    } // undefined if endpoint error
    if (d?.status !== 200) return null;

    const data = d.data['com.sittercity.profile:Provider'];

    const childcare = data['com.sittercity.profile:Childcare'];

    const ProfileData = {
      badges: data['com.sittercity.profile:Badges'],
      childcare: {
        childcareAdditionalServices: childcare.childcare_additional_services,
        childcareAgeExperience: childcare.childcare_age_experience,
        picture: childcare['com.sittercity.profile:Picture'],
        babySitter: childcare['com.sittercity.profile:Childcare.Babysitter'],
        willingToDriveChildren: childcare.willing_to_drive_children,
        workWithTwins: childcare.work_with_twins,
        yearsOfExperience: childcare.years_of_experience,
      },
      createdAt: data.created_at,
      firstName: data.first_name,
      lastName: data.last_name,
      lastAuthenticationAt: data.last_authentication_at,
      updatedAt: data.updatedAt,
    };

    return new Profile(ProfileData);
  }

  public async closeTheLoopData(): Promise<CloseTheLoop | null | undefined> {
    const d = await this.fetchData('/close-the-loop-data');
    if (d === undefined) {
      return undefined;
    } // undefined if endpoint error
    if (d?.status !== 200) return null;

    const data = d.data['com.sittercity.account:CloseTheLoop'];

    const CloseTheLoopData = {
      hasActiveIapSubscription: data.has_active_iap_subscription,
      hasCanceledSubscription: data.has_canceled_subscription,
      isWinBackEligible: data.is_win_back_eligible,
      contactedMembers: data.contacted_members,
    };
    return new CloseTheLoop(CloseTheLoopData);
  }
}
